import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { WalletIcon, ShieldCheckIcon, CubeTransparentIcon, BoltIcon } from '@heroicons/react/24/outline';
import proofedBadge from '../assets/proofed-by-hacken.webp';
import businesswoman from '../assets/businesswoman-bitcoin.jpg';

const Web3Section = () => {
  const features = [
    {
      icon: WalletIcon,
      title: "Connect Your Wallet",
      description: "Seamlessly integrate with MetaMask, WalletConnect, and 50+ Web3 wallets"
    },
    {
      icon: ShieldCheckIcon,
      title: "Bank-Grade Security",
      description: "Multi-signature protection and insurance coverage up to $100M"
    },
    {
      icon: CubeTransparentIcon,
      title: "DeFi Integration",
      description: "Access lending, staking, and yield farming directly from your portfolio"
    },
    {
      icon: BoltIcon,
      title: "Instant Settlements",
      description: "Lightning-fast blockchain transactions with minimal gas fees"
    }
  ];

  const stats = [
    { value: "100+", label: "Supported Cryptocurrencies" },
    { value: "$5B+", label: "In Digital Assets" },
    { value: "24/7", label: "Blockchain Monitoring" }
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-gray-900 via-amber-900 to-gray-900 text-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30"
            >
              <CubeTransparentIcon className="w-5 h-5 text-amber-400 mr-2" />
              <span className="text-sm font-semibold text-amber-400">Web3 Integration</span>
            </motion.div>

            {/* Title */}
            <div>
              <h2 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                Welcome to the Future of Finance
              </h2>
              <p className="text-xl text-gray-300">
                Experience decentralized finance (DeFi) with institutional-grade security and support
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-3 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1 text-sm">{feature.title}</h3>
                      <p className="text-xs text-gray-400">{feature.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="text-3xl lg:text-4xl font-bold text-amber-400 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-400">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Security Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="flex items-center space-x-4 p-4 rounded-xl bg-white/5 border border-white/10"
            >
              <img src={proofedBadge} alt="Proofed by Hacken" className="h-12 w-auto" />
              <div>
                <p className="font-semibold text-sm">Security Audited</p>
                <p className="text-xs text-gray-400">Smart contracts verified by leading security firms</p>
              </div>
            </motion.div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link
                to="/web3-connect"
                className="inline-flex items-center justify-center px-8 py-4 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                <WalletIcon className="w-5 h-5 mr-2" />
                Connect Wallet
              </Link>
               <Link 
                 to="/web3"
                className="inline-flex items-center justify-center px-8 py-4 rounded-2xl border-2 border-white/30 text-white font-semibold hover:bg-white/10 transition-all duration-300"
              >
                Learn More
                 </Link>
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={businesswoman}
                alt="Web3 Integration"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-amber-600/30 to-orange-600/30"></div>
              
              {/* Floating Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                viewport={{ once: true }}
                className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Portfolio Value</p>
                    <p className="text-3xl font-bold text-gray-900">$127,843.92</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                    <WalletIcon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex items-center text-amber-600">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                  <span className="text-sm font-semibold">+23.7% This Month</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Web3Section; 
